import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, FileText, Send, Video, Calendar as CalendarIcon, Phone, X as XIcon } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { CallRoom } from '@/components/CallRoom';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth';
import { cn, formatBRL, formatRelativeDate, getInitials } from '@/lib/utils';
import { TASK_TYPE_LABELS, type TaskType, type TaskStatus } from '@parecer/shared';

interface Proposal {
  scope: string;
  deadlineDays: number;
  priceAmount: number; // cents (lawyer net)
  message?: string;
  sentAt: string;
  acceptedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
}

interface TaskDetail {
  taskId: string;
  ownerId: string;
  ownerName: string | null;
  ownerEmail: string | null;
  lawyerId?: string;
  lawyerName: string | null;
  lawyerEmail: string | null;
  type: TaskType;
  status: TaskStatus;
  description: string;
  totalAmount: number;
  priceAmount: number;
  platformFeeAmount: number;
  deadlineDays?: number;
  proposal?: Proposal;
  createdAt: string;
  updatedAt: string;
  calls?: CallItem[];
}

interface MessageItem {
  messageId: string;
  taskId: string;
  senderId: string;
  text: string;
  read: boolean;
  createdAt: string;
}

interface CallItem {
  callId: string;
  taskId: string;
  dailyRoomName: string;
  dailyRoomUrl: string;
  scheduledAt: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
}

interface CallWithToken extends CallItem {
  meetingToken: string;
}

export function TarefaDetalhe() {
  const { taskId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role) ?? 'users';
  const userId = useAuthStore((s) => s.user?.userId);
  const userEmail = useAuthStore((s) => s.user?.email);
  const qc = useQueryClient();
  const { toast } = useToast();

  const [draft, setDraft] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [activeCall, setActiveCall] = useState<CallWithToken | null>(null);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [propScope, setPropScope] = useState('');
  const [propDeadline, setPropDeadline] = useState<number | ''>(7);
  const [propPriceReais, setPropPriceReais] = useState<string>('');
  const [propMessage, setPropMessage] = useState('');

  const { data: task } = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => api.get<TaskDetail>(`/tasks/${taskId}`),
    enabled: Boolean(taskId),
  });

  const { data: messages } = useQuery({
    queryKey: ['task', taskId, 'messages'],
    queryFn: () => api.get<{ items: MessageItem[] }>(`/tasks/${taskId}/messages?limit=100`),
    enabled: Boolean(taskId),
    refetchInterval: 5000,
  });

  const calls: CallItem[] = task?.calls ?? [];

  const sendMut = useMutation({
    mutationFn: (text: string) => api.post(`/tasks/${taskId}/messages`, { text }),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['task', taskId, 'messages'] });
    },
    onError: (err) => {
      toast({ variant: 'danger', title: 'Falha ao enviar', description: err instanceof Error ? err.message : 'Erro' });
    },
  });

  const scheduleMut = useMutation({
    mutationFn: (scheduledAt: string) =>
      api.post<{ callId: string; roomUrl: string; roomName: string; scheduledAt: string }>(
        `/tasks/${taskId}/calls`,
        { scheduledAt },
      ),
    onSuccess: (data) => {
      toast({
        variant: 'success',
        title: 'Call agendada',
        description: `Sala ${data.roomName} criada. Email enviado às partes.`,
      });
      setScheduleOpen(false);
      setScheduleDate('');
      qc.invalidateQueries({ queryKey: ['task', taskId] });
    },
    onError: (err) => {
      toast({ variant: 'danger', title: 'Falha ao agendar', description: err instanceof Error ? err.message : 'Erro' });
    },
  });

  const joinMut = useMutation({
    mutationFn: (callId: string) => api.get<CallWithToken>(`/tasks/${taskId}/calls/${callId}`),
    onSuccess: (data) => setActiveCall(data),
    onError: (err) => {
      toast({ variant: 'danger', title: 'Falha ao entrar', description: err instanceof Error ? err.message : 'Erro' });
    },
  });

  const submitProposalMut = useMutation({
    mutationFn: (body: { scope: string; deadlineDays: number; priceAmount: number; message?: string }) =>
      api.post(`/tasks/${taskId}/proposal`, body),
    onSuccess: () => {
      toast({ variant: 'success', title: 'Proposta enviada', description: 'Cliente recebeu — aguarde aceite.' });
      setProposalOpen(false);
      setPropScope('');
      setPropDeadline(7);
      setPropPriceReais('');
      setPropMessage('');
      qc.invalidateQueries({ queryKey: ['task', taskId] });
    },
    onError: (err) =>
      toast({
        variant: 'danger',
        title: 'Falha ao enviar proposta',
        description: err instanceof Error ? err.message : 'Erro',
      }),
  });

  const acceptProposalMut = useMutation({
    mutationFn: () => api.post<{ taskId: string; nextStep: string }>(`/tasks/${taskId}/accept-proposal`),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Proposta aceita',
        description: 'Redirecionando para o checkout…',
      });
      qc.invalidateQueries({ queryKey: ['task', taskId] });
      // Frontend already has a checkout flow at /payments/checkout via the existing tasks integration.
      // We push the cliente straight to the checkout route — payments-webhook
      // will flip status to in_progress after payment confirms.
      navigate(`/app/tarefas/${taskId}?checkout=1`);
    },
    onError: (err) =>
      toast({
        variant: 'danger',
        title: 'Falha ao aceitar',
        description: err instanceof Error ? err.message : 'Erro',
      }),
  });

  const rejectProposalMut = useMutation({
    mutationFn: (reason?: string) => api.post(`/tasks/${taskId}/reject-proposal`, { reason }),
    onSuccess: () => {
      toast({ variant: 'success', title: 'Proposta recusada' });
      qc.invalidateQueries({ queryKey: ['task', taskId] });
    },
    onError: (err) =>
      toast({
        variant: 'danger',
        title: 'Falha ao recusar',
        description: err instanceof Error ? err.message : 'Erro',
      }),
  });

  function handleSubmitProposal(e: React.FormEvent): void {
    e.preventDefault();
    const priceAmount = Math.round(parseFloat(propPriceReais || '0') * 100);
    if (!propScope.trim() || propScope.length < 30 || typeof propDeadline !== 'number' || priceAmount <= 0) {
      toast({ variant: 'warning', title: 'Preencha todos os campos' });
      return;
    }
    submitProposalMut.mutate({
      scope: propScope.trim(),
      deadlineDays: propDeadline,
      priceAmount,
      ...(propMessage.trim() ? { message: propMessage.trim() } : {}),
    });
  }

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages?.items.length]);

  // Auto-open call from email deep-link (?call=:callId).
  // We wait until the calls list is loaded to confirm the call exists, then
  // strip the query param so a refresh doesn't re-trigger the join.
  useEffect(() => {
    const targetCallId = searchParams.get('call');
    if (!targetCallId || !taskId || activeCall) return;
    const exists = calls.some((c) => c.callId === targetCallId);
    if (!exists) return;
    joinMut.mutate(targetCallId, {
      onSuccess: () => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('call');
            return next;
          },
          { replace: true },
        );
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, taskId, calls.length]);

  const minDateTime = new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16);

  function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    sendMut.mutate(draft.trim());
  }

  function handleSchedule(e: React.FormEvent) {
    e.preventDefault();
    if (!scheduleDate) return;
    const iso = new Date(scheduleDate).toISOString();
    scheduleMut.mutate(iso);
  }

  return (
    <AppShell role={role === 'lawyers' ? 'lawyers' : 'users'}>
      <div className="max-w-6xl">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-mono">{taskId}</p>
            <h1 className="font-serif text-display-lg mt-1">
              {task ? TASK_TYPE_LABELS[task.type] : 'Carregando…'}
            </h1>
            {task && (
              <>
                <p className="mt-2 text-sm text-muted-foreground">
                  <span className="text-foreground font-medium">{task.ownerName ?? 'Cliente'}</span>
                  {task.lawyerName && (
                    <>
                      {' '}
                      <span className="text-muted-foreground">↔</span>{' '}
                      <span className="text-foreground font-medium">{task.lawyerName}</span>
                    </>
                  )}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                  <StatusBadge status={task.status} />
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">
                    Criada {formatRelativeDate(task.createdAt)}
                  </span>
                  {task.totalAmount > 0 && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-medium">{formatBRL(task.totalAmount)}</span>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setScheduleOpen(true)} variant="accent">
              <CalendarIcon className="h-4 w-4 mr-2" />
              Agendar call
            </Button>
          </div>
        </div>

        {task?.description && (
          <Card className="mt-6">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground whitespace-pre-line text-pretty">
                {task.description}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Proposal card — visible when lawyer offered scope+price+deadline */}
        {task && task.proposal && task.status === 'proposed' && (
          <Card className="mt-6 border-accent/40 bg-accent/[0.04]">
            <CardHeader className="py-5">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4 text-accent" />
                Proposta do advogado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md bg-card/60 border border-border/40 p-4 text-sm space-y-2">
                <p className="text-pretty whitespace-pre-line">{task.proposal.scope}</p>
                {task.proposal.message && (
                  <p className="text-muted-foreground text-pretty whitespace-pre-line border-l-2 border-accent/40 pl-3 mt-3 italic">
                    {task.proposal.message}
                  </p>
                )}
              </div>
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                <div className="rounded-md border border-border/60 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Escopo</p>
                  <p className="mt-1 font-medium">Conforme descrito acima</p>
                </div>
                <div className="rounded-md border border-border/60 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Prazo</p>
                  <p className="mt-1 font-medium">{task.proposal.deadlineDays} dia(s)</p>
                </div>
                <div className="rounded-md border border-accent/40 bg-accent/5 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Você paga</p>
                  <p className="mt-1 font-serif text-lg">
                    {formatBRL(Math.round(task.proposal.priceAmount * 1.15))}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Advogado recebe {formatBRL(task.proposal.priceAmount)} · taxa 15%
                  </p>
                </div>
              </div>
              {role === 'users' && (
                <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const reason = window.prompt('Motivo da recusa (opcional)') ?? undefined;
                      rejectProposalMut.mutate(reason || undefined);
                    }}
                    loading={rejectProposalMut.isPending}
                    className="gap-2"
                  >
                    <XIcon className="h-4 w-4" /> Recusar
                  </Button>
                  <Button
                    type="button"
                    variant="accent"
                    onClick={() => acceptProposalMut.mutate()}
                    loading={acceptProposalMut.isPending}
                    className="flex-1 gap-2"
                  >
                    <Check className="h-4 w-4" /> Aceitar e ir para pagamento
                  </Button>
                </div>
              )}
              {role === 'lawyers' && (
                <p className="text-xs text-muted-foreground">
                  Proposta enviada {formatRelativeDate(task.proposal.sentAt)} — aguardando cliente.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Lawyer CTA — "Enviar proposta" when no proposal yet */}
        {task &&
          role === 'lawyers' &&
          task.lawyerId === userId &&
          (task.status === 'open' || task.status === 'assigned') && (
            <Card className="mt-6 border-dashed">
              <CardContent className="pt-6 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-medium">Pronto pra fechar?</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Envie uma proposta formal com escopo, prazo e preço. Cliente aceita ou recusa.
                  </p>
                </div>
                <Button variant="accent" onClick={() => setProposalOpen(true)} className="gap-2">
                  <FileText className="h-4 w-4" /> Enviar proposta
                </Button>
              </CardContent>
            </Card>
          )}

        <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_320px]">
          {/* Chat column */}
          <Card className="flex flex-col h-[600px]">
            <CardHeader className="border-b border-border/40 py-4">
              <CardTitle className="text-base">Conversa</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6 space-y-3">
                {!messages && <p className="text-sm text-muted-foreground">Carregando…</p>}
                {messages && messages.items.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">Sem mensagens. Comece a conversa.</p>
                )}
                {messages?.items
                  .slice()
                  .reverse()
                  .map((m, idx, arr) => {
                    const mine = m.senderId === userId;
                    const senderName =
                      m.senderId === task?.ownerId
                        ? task?.ownerName ?? 'Cliente'
                        : m.senderId === task?.lawyerId
                          ? task?.lawyerName ?? 'Advogado'
                          : '?';
                    const prev = idx > 0 ? arr[idx - 1] : null;
                    const isFirstInRun = !prev || prev.senderId !== m.senderId;
                    return (
                      <div
                        key={m.messageId}
                        className={cn(
                          'flex',
                          mine ? 'justify-end' : 'justify-start',
                          isFirstInRun ? 'mt-3' : 'mt-0.5',
                        )}
                      >
                        {/* Avatar only for theirs, only on the first message of a run */}
                        {!mine && (
                          <div className="w-7 mr-2 flex-none flex items-end">
                            {isFirstInRun ? (
                              <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-[10px]">
                                {getInitials(senderName)}
                              </div>
                            ) : null}
                          </div>
                        )}
                        <div className={cn('max-w-[78%] flex flex-col', mine ? 'items-end' : 'items-start')}>
                          {!mine && isFirstInRun && (
                            <span className="text-[11px] text-muted-foreground mb-1 px-1">
                              {senderName}
                            </span>
                          )}
                          <div
                            className={cn(
                              'px-3 py-2 text-sm shadow-sm',
                              mine
                                ? 'bg-accent/20 text-foreground border border-accent/25'
                                : 'bg-card text-foreground border border-border/60',
                              // WhatsApp-style asymmetric tail: flat corner on the side
                              // facing the conversation partner only on the first bubble
                              // of a run; subsequent ones are fully rounded.
                              mine
                                ? isFirstInRun
                                  ? 'rounded-2xl rounded-tr-sm'
                                  : 'rounded-2xl'
                                : isFirstInRun
                                  ? 'rounded-2xl rounded-tl-sm'
                                  : 'rounded-2xl',
                            )}
                          >
                            <p className="whitespace-pre-wrap break-words">{m.text}</p>
                            <span className="block mt-0.5 text-[10px] text-muted-foreground/80 text-right">
                              {formatRelativeDate(m.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                <div ref={messagesEndRef} />
              </div>
              <form onSubmit={handleSend} className="border-t border-border/40 p-3 flex gap-2">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Digite sua mensagem…"
                  disabled={sendMut.isPending}
                />
                <Button type="submit" disabled={!draft.trim() || sendMut.isPending} loading={sendMut.isPending}>
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Right column: calls + status */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Video className="h-4 w-4 text-accent" />
                  Calls
                </CardTitle>
              </CardHeader>
              <CardContent>
                {calls.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Nenhuma call agendada. Use <strong>Agendar call</strong> no topo.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {calls.map((c) => (
                      <li key={c.callId} className="border rounded-md p-3">
                        <p className="text-sm font-medium">
                          {new Date(c.scheduledAt).toLocaleString('pt-BR', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Sala: <span className="font-mono">{c.dailyRoomName}</span>
                        </p>
                        <Button
                          size="sm"
                          variant="accent"
                          className="mt-2 w-full"
                          onClick={() => joinMut.mutate(c.callId)}
                          loading={joinMut.isPending}
                        >
                          <Phone className="h-3 w-3 mr-2" />
                          Entrar na sala
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {task && (
              <Card>
                <CardHeader className="py-4">
                  <CardTitle className="text-base">Detalhes</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  <Row k="Tipo" v={TASK_TYPE_LABELS[task.type]} />
                  <Row k="Status" v={<StatusBadge status={task.status} />} />
                  <Row k="Cliente" v={task.ownerName ?? '—'} />
                  {task.lawyerId && (
                    <Row k="Advogado" v={task.lawyerName ?? '—'} />
                  )}
                  {task.totalAmount > 0 && <Row k="Valor" v={formatBRL(task.totalAmount)} />}
                  {task.deadlineDays && <Row k="Prazo" v={`${task.deadlineDays} dias`} />}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Schedule call dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agendar call</DialogTitle>
            <DialogDescription>
              Sala Daily.co criada automaticamente. Ambas as partes recebem email com o link.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSchedule} className="space-y-4">
            <div>
              <Label htmlFor="scheduledAt">Data e horário</Label>
              <Input
                id="scheduledAt"
                type="datetime-local"
                required
                min={minDateTime}
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="mt-1.5"
              />
              <p className="text-xs text-muted-foreground mt-1">
                A sala expira 2 horas após o horário agendado.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setScheduleOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="accent" loading={scheduleMut.isPending}>
                Agendar e notificar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Proposal compose dialog (lawyer) */}
      <Dialog open={proposalOpen} onOpenChange={setProposalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar proposta</DialogTitle>
            <DialogDescription>
              Defina escopo, prazo e preço. Cliente aceita → checkout → escrow. Recusa → volta pra
              "atribuída" e você pode mandar nova.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitProposal} className="space-y-4">
            <div>
              <Label htmlFor="prop-scope">Escopo (o que você vai entregar)</Label>
              <textarea
                id="prop-scope"
                rows={5}
                minLength={30}
                maxLength={3000}
                className="mt-1.5 flex w-full rounded-md border border-input bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Ex: Revisão completa do contrato anexo com (i) análise cláusula por cláusula, (ii) sugestão de redação para 3 cláusulas críticas, (iii) call de 30 min pra explicar mudanças."
                value={propScope}
                onChange={(e) => setPropScope(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground mt-1">{propScope.length} / 3000 (mín. 30)</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="prop-deadline">Prazo (dias)</Label>
                <Input
                  id="prop-deadline"
                  type="number"
                  min={1}
                  max={180}
                  className="mt-1.5"
                  value={propDeadline}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPropDeadline(v === '' ? '' : Number(v));
                  }}
                />
              </div>
              <div>
                <Label htmlFor="prop-price">Preço (R$ líquido pra você)</Label>
                <Input
                  id="prop-price"
                  type="number"
                  min={1}
                  step="0.01"
                  placeholder="500,00"
                  className="mt-1.5"
                  value={propPriceReais}
                  onChange={(e) => setPropPriceReais(e.target.value)}
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Cliente paga +15% (taxa plataforma).
                </p>
              </div>
            </div>
            <div>
              <Label htmlFor="prop-message">Mensagem (opcional)</Label>
              <textarea
                id="prop-message"
                rows={2}
                maxLength={2000}
                className="mt-1.5 flex w-full rounded-md border border-input bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Algo que queira dizer antes do cliente decidir…"
                value={propMessage}
                onChange={(e) => setPropMessage(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setProposalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="accent" loading={submitProposalMut.isPending}>
                Enviar proposta
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* In-call dialog (full-screen Daily iframe) */}
      <Dialog open={Boolean(activeCall)} onOpenChange={(open) => !open && setActiveCall(null)}>
        <DialogContent className="max-w-6xl h-[80vh] p-0 flex flex-col" hideClose>
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
            <DialogTitle className="text-base">
              <Video className="h-4 w-4 inline mr-2 text-accent" />
              {activeCall?.dailyRoomName}
            </DialogTitle>
            <Button size="sm" variant="outline" onClick={() => setActiveCall(null)}>
              Sair da sala
            </Button>
          </div>
          <div className="flex-1 overflow-hidden">
            {activeCall && (
              <CallRoom
                roomUrl={activeCall.dailyRoomUrl}
                meetingToken={activeCall.meetingToken}
                userName={userEmail ?? 'Participante'}
                onLeft={() => setActiveCall(null)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground text-xs uppercase tracking-wide">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { variant: 'accent' | 'success' | 'warning' | 'danger' | 'muted'; label: string }> = {
    open: { variant: 'warning', label: 'Aberta' },
    assigned: { variant: 'warning', label: 'Atribuída' },
    proposed: { variant: 'accent', label: 'Proposta enviada' },
    in_progress: { variant: 'warning', label: 'Em andamento' },
    delivered: { variant: 'success', label: 'Entregue' },
    completed: { variant: 'success', label: 'Concluída' },
    rejected: { variant: 'muted', label: 'Rejeitada' },
    cancelled: { variant: 'muted', label: 'Cancelada' },
    disputed: { variant: 'danger', label: 'Em disputa' },
  };
  const info = map[status] ?? { variant: 'muted' as const, label: status };
  return <Badge variant={info.variant}>{info.label}</Badge>;
}
